import {
  ArrayPath,
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  UseFormRegister,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { Lock, Plus, RotateCcw, Trash2 } from "lucide-react";
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Switch } from "components/ui/switch";
import { SelectField, SelectOption } from "components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";

import {
  CampaignType,
  VOUCHER_VALIDITY_MODE_LABELS,
  VoucherValidityMode,
} from "@/@types/gamification";
import {
  DEFAULT_GOAL_PROBABILITY,
  LOCKED_MIN_PURCHASE,
  DEFAULT_SLOT_SYMBOLS,
  SLOT_ICON_CATALOG,
} from "../constants/mechanic-defaults";
import {
  COMPETING_CAMPAIGNS_COPY,
  MIN_PURCHASE_LOCK_COPY,
} from "../constants/messages";
import { formPriorityLevelOf } from "../constants/priority-levels";
import { ConcurrencyGroupField } from "./concurrency-group-field";
import { PriorityConcurrencyCard } from "./priority-concurrency-card";
import { PriorityLevelPicker } from "./priority-level-picker";

interface MechanicConfigSectionProps<T extends FieldValues> {
  campaignType: CampaignType;
  control: Control<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;

  currentCampaignId?: number;
  currentCreatedAt?: string;

  generalLead?: ReactNode;
}

type FieldError = { message?: string };

type MechanicConfigErrors = {
  mechanicConfig?: {
    maxPlays?: FieldError;
    minPurchase?: FieldError;
    priority?: FieldError;
    concurrency?: { group?: FieldError };
    voucherValidity?: {
      validityDays?: FieldError;
      startOffsetDays?: FieldError;
    };
    mechanicParams?: {
      goalProbability?: FieldError;
      symbols?: Array<{
        id?: FieldError;
        label?: FieldError;
        value?: FieldError;
      }> & { message?: string };
    };
  };
};

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? <p className="mt-1 text-sm text-red-500">{message}</p> : null;

const VALIDITY_MODE_ORDER: VoucherValidityMode[] = [
  VoucherValidityMode.DAYS,
  VoucherValidityMode.END_OF_NEXT_MONTH,
  VoucherValidityMode.CAMPAIGN_END,
];

const VALIDITY_MODE_OPTIONS: SelectOption[] = VALIDITY_MODE_ORDER.map(
  (mode) => ({ id: mode, value: mode, label: VOUCHER_VALIDITY_MODE_LABELS[mode] }),
);

const SLOT_ICON_OPTIONS: SelectOption[] = SLOT_ICON_CATALOG.map((icon) => ({
  id: icon.value,
  value: icon.value,
  label: icon.label,
}));

export function MechanicConfigSection<T extends FieldValues>({
  campaignType,
  control,
  register,
  errors,
  currentCampaignId,
  currentCreatedAt,
  generalLead,
}: MechanicConfigSectionProps<T>) {
  const mcErrors = (errors as MechanicConfigErrors).mechanicConfig;
  const path = (p: string) => p as Path<T>;

  const symbols = useFieldArray({
    control,
    name: "mechanicConfig.mechanicParams.symbols" as ArrayPath<T>,
  });

  const competes = Boolean(
    useWatch({ control, name: path("mechanicConfig.concurrency.competes") }),
  );
  const [startDate, endDate, priority, group] = useWatch({
    control,
    name: [
      path("startDate"),
      path("endDate"),
      path("mechanicConfig.priority"),
      path("mechanicConfig.concurrency.group"),
    ],
  }) as [string | undefined, string | undefined, unknown, string | null | undefined];
  const validityMode =
    (useWatch({
      control,
      name: path("mechanicConfig.voucherValidity.mode"),
    }) as VoucherValidityMode | undefined) ?? VoucherValidityMode.DAYS;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Parâmetros gerais</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {generalLead}
          <div>
            <Label htmlFor="mc-maxPlays">Jogadas por CPF</Label>
            <Input
              id="mc-maxPlays"
              type="number"
              min="1"
              max="999"
              placeholder="3"
              onWheel={(e) => e.currentTarget.blur()}
              {...register(path("mechanicConfig.maxPlays"))}
            />
            <FieldErrorText message={mcErrors?.maxPlays?.message} />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <Label htmlFor="mc-minPurchase" className="mb-0">
                {MIN_PURCHASE_LOCK_COPY.label}
              </Label>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={MIN_PURCHASE_LOCK_COPY.lockedHint}
                    >
                      <Lock aria-hidden="true" className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{MIN_PURCHASE_LOCK_COPY.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id="mc-minPurchase"
              type="number"
              readOnly
              aria-readonly="true"
              aria-describedby="mc-minPurchase-help"
              placeholder={String(LOCKED_MIN_PURCHASE)}
              className="cursor-not-allowed bg-muted text-muted-foreground"
              {...register(path("mechanicConfig.minPurchase"))}
            />
            <p id="mc-minPurchase-help" className="mt-1 text-xs text-muted-foreground">
              {MIN_PURCHASE_LOCK_COPY.help}
            </p>
            <FieldErrorText message={mcErrors?.minPurchase?.message} />
          </div>
        </CardContent>
      </Card>

      <PriorityConcurrencyCard
        priorityHasError={Boolean(mcErrors?.priority?.message)}
        concurrencyHasError={Boolean(mcErrors?.concurrency?.group?.message)}
        competing={{
          campaignType,
          startDate,
          endDate,
          priority: formPriorityLevelOf(priority),
          competes,
          group,
          currentCampaignId,
          currentCreatedAt,
        }}
        priorityField={
          <div className="space-y-2">
            <p id="mc-priority-label" className="sr-only">
              {COMPETING_CAMPAIGNS_COPY.priorityLabel}
            </p>
            <p className="text-sm text-muted-foreground">
              {COMPETING_CAMPAIGNS_COPY.priorityHelp}
            </p>
            <Controller
              control={control}
              name={path("mechanicConfig.priority")}
              render={({ field }) => (
                <PriorityLevelPicker
                  id="mc-priority"
                  labelledBy="mc-priority-label"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errorMessage={mcErrors?.priority?.message}
                />
              )}
            />
          </div>
        }
        concurrencyField={
          <>
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name={path("mechanicConfig.concurrency.competes")}
                render={({ field }) => (
                  <Switch
                    id="mc-competes"
                    checked={Boolean(field.value)}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Label htmlFor="mc-competes" className="mb-0">
                Concorre com outras campanhas?
              </Label>
            </div>

            {competes && (
              <ConcurrencyGroupField
                control={control}
                name={path("mechanicConfig.concurrency.group")}
                errorMessage={mcErrors?.concurrency?.group?.message}
                currentCampaignId={currentCampaignId}
              />
            )}

            {!competes && (
              <Badge
                variant="outline"
                className="bg-slate-50 text-slate-700 border-slate-200"
              >
                Campanha paralela — roda junto das demais
              </Badge>
            )}
          </>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Validade do voucher</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <Label htmlFor="mc-validity-mode">Modo de validade</Label>
            <Controller
              control={control}
              name={path("mechanicConfig.voucherValidity.mode")}
              defaultValue={VoucherValidityMode.DAYS as never}
              render={({ field }) => (
                <SelectField
                  id="mc-validity-mode"
                  options={VALIDITY_MODE_OPTIONS}
                  value={(field.value as string) ?? VoucherValidityMode.DAYS}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {validityMode === VoucherValidityMode.DAYS && (
            <div>
              <Label htmlFor="mc-validity-days">Validade (dias) *</Label>
              <Input
                id="mc-validity-days"
                type="number"
                min="1"
                placeholder="30"
                onWheel={(e) => e.currentTarget.blur()}
                {...register(path("mechanicConfig.voucherValidity.validityDays"))}
              />
              {mcErrors?.voucherValidity?.validityDays?.message && (
                <p className="mt-1 text-sm text-red-500">
                  {mcErrors.voucherValidity.validityDays.message}
                </p>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="mc-validity-start">Dias para início</Label>
            <Input
              id="mc-validity-start"
              type="number"
              min="0"
              placeholder="0"
              onWheel={(e) => e.currentTarget.blur()}
              {...register(
                path("mechanicConfig.voucherValidity.startOffsetDays"),
              )}
            />
            <p className="mt-1 text-xs text-gray-500">
              Dias após a emissão para o voucher começar a valer.
            </p>
            {mcErrors?.voucherValidity?.startOffsetDays?.message && (
              <p className="mt-1 text-sm text-red-500">
                {mcErrors.voucherValidity.startOffsetDays.message}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {campaignType === CampaignType.PENALTY && (
        <Card>
          <CardHeader>
            <CardTitle>Configuração — Gol Premiado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-w-xs">
              <Label htmlFor="mc-goalProbability">Probabilidade de gol (0 a 1)</Label>
              <Input
                id="mc-goalProbability"
                type="number"
                min="0"
                max="1"
                step="0.01"
                placeholder={String(DEFAULT_GOAL_PROBABILITY)}
                onWheel={(e) => e.currentTarget.blur()}
                {...register(path("mechanicConfig.mechanicParams.goalProbability"))}
              />
              <p className="mt-1 text-xs text-gray-500">
                Padrão: {DEFAULT_GOAL_PROBABILITY}.
              </p>
              {mcErrors?.mechanicParams?.goalProbability && (
                <p className="mt-1 text-sm text-red-500">
                  {mcErrors.mechanicParams.goalProbability.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {campaignType === CampaignType.SLOT && (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-y-2">
            <CardTitle>Símbolos — Caça-níquel</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => symbols.replace(DEFAULT_SLOT_SYMBOLS as never[])}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Resetar Padrão
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  symbols.append({ id: "", label: "", value: 0, icon: "" } as never)
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Adicionar símbolo
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {symbols.fields.map((field, index) => {
              const symbolError = mcErrors?.mechanicParams?.symbols?.[index];
              return (
                <div
                  key={field.id}
                  className="grid grid-cols-1 items-start gap-4 rounded-lg border p-4 md:grid-cols-[1fr_1fr_1fr_1fr_auto]"
                >
                  <div>
                    <Label htmlFor={`sym-${index}-id`}>ID</Label>
                    <Input
                      id={`sym-${index}-id`}
                      placeholder="seven"
                      {...register(path(`mechanicConfig.mechanicParams.symbols.${index}.id`))}
                    />
                    <FieldErrorText message={symbolError?.id?.message} />
                  </div>
                  <div>
                    <Label htmlFor={`sym-${index}-label`}>Rótulo</Label>
                    <Input
                      id={`sym-${index}-label`}
                      placeholder="7"
                      {...register(path(`mechanicConfig.mechanicParams.symbols.${index}.label`))}
                    />
                    <FieldErrorText message={symbolError?.label?.message} />
                  </div>
                  <div>
                    <Label htmlFor={`sym-${index}-icon`}>Ícone</Label>
                    <Controller
                      control={control}
                      name={path(
                        `mechanicConfig.mechanicParams.symbols.${index}.icon`,
                      )}
                      defaultValue={SLOT_ICON_CATALOG[0].value as never}
                      render={({ field }) => (
                        <SelectField
                          id={`sym-${index}-icon`}
                          options={SLOT_ICON_OPTIONS}
                          value={
                            (field.value as string) ?? SLOT_ICON_CATALOG[0].value
                          }
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`sym-${index}-value`}>Valor (R$)</Label>
                    <Input
                      id={`sym-${index}-value`}
                      type="number"
                      min="0"
                      step="0.01"
                      onWheel={(e) => e.currentTarget.blur()}
                      {...register(path(`mechanicConfig.mechanicParams.symbols.${index}.value`))}
                    />
                    <FieldErrorText message={symbolError?.value?.message} />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="md:mt-6"
                    onClick={() => symbols.remove(index)}
                    aria-label="Remover símbolo"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
            {mcErrors?.mechanicParams?.symbols?.message && (
              <p className="text-sm text-red-500">
                {mcErrors.mechanicParams.symbols.message}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
